// src/components/BookCover/CollaboratorBar.jsx
// Per-version collaborator management bar.
// Author opens a dialog, searches users, selects a group, picks a role for all, and adds them at once.
// The dialog stays open so the author can add another group with a different role.

import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Avatar, Tooltip, IconButton,
  TextField, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButtonGroup, ToggleButton,
  Chip, Divider,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import BrushIcon from '@mui/icons-material/Brush';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_BASE } from '../../utils/api';

function isExpired(d) { return d ? new Date(d) < new Date() : false; }
function isOverdue(d) { return d ? new Date(d) < new Date() : false; }

const ROLE_CONFIG = {
  design: { label: 'Designer', icon: '🎨', color: '#8b5cf6', bg: '#ede9fe', border: '#c4b5fd' },
  view_only: { label: 'Viewer', icon: '👁️', color: '#0ea5e9', bg: '#e0f2fe', border: '#7dd3fc' },
};

export default function CollaboratorBar({
  draftId,
  versionId,
  collaborators = [],
  isAuthor = false,
  onCollaboratorsChange,
  embedded = false,
  openAddDialogSignal = 0,
  hideButton = false,
  hideList = false,
}) {
  const [profilePictures, setProfilePictures] = useState({});

  // Unified add dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]); // selected but not yet added
  const [groupRole, setGroupRole] = useState('design');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteDueDate, setInviteDueDate] = useState('');
  const [inviteExpirationDate, setInviteExpirationDate] = useState('');
  const [adding, setAdding] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(''); // brief success message

  // Outgoing invitations state (to show status)
  const [outgoingInvites, setOutgoingInvites] = useState([]);

  // Fetch profile pictures for existing collaborators
  useEffect(() => {
    if (!collaborators.length) return;
    const fetchPics = async () => {
      const pics = {};
      for (const c of collaborators) {
        const uid = c.userId;
        if (!uid || pics[uid]) continue;
        try {
          const token = localStorage.getItem('rv_access_token');
          const res = await fetch(`${API_BASE}/api/profile/user/${uid}/picture-url`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.url) pics[uid] = data.url;
          }
        } catch { /* ignore */ }
      }
      setProfilePictures(pics);
    };
    fetchPics();
  }, [collaborators]);

  // Load outgoing invitations for this version
  useEffect(() => {
    if (!draftId || !versionId || !isAuthor) return;
    const fetchInvites = async () => {
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(
          `${API_BASE}/api/invitations/outgoing?draftId=${draftId}&versionId=${versionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setOutgoingInvites(Array.isArray(data) ? data : []);
        }
      } catch { /* ignore */ }
    };
    fetchInvites();
  }, [draftId, versionId, isAuthor]);

  // Auto-search (debounced) — exclude already-added and pending users
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/users/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const existingIds = new Set([
            ...collaborators.map(c => c.userId),
            ...pendingUsers.map(u => u._id || u.id),
          ]);
          const filtered = (data.users || data || []).filter(u => !existingIds.has(u._id || u.id));
          setSearchResults(filtered);
        }
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, collaborators, pendingUsers]);

  const handleOpenDialog = () => {
    setPendingUsers([]);
    setGroupRole('design');
    setSearchQuery('');
    setSearchResults([]);
    setAddedFeedback('');
    setInviteMessage('');
    setDialogOpen(true);
  };

  // External trigger: when openAddDialogSignal changes (and is truthy), open the dialog.
  useEffect(() => {
    if (openAddDialogSignal && isAuthor) {
      handleOpenDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddDialogSignal]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPendingUsers([]);
    setSearchQuery('');
    setSearchResults([]);
    setInviteMessage('');
    setInviteDueDate('');
    setInviteExpirationDate('');
    setAddedFeedback('');
  };

  // Toggle a user in/out of the pending selection
  const handleTogglePending = (user) => {
    const uid = user._id || user.id;
    const isAlreadyPending = pendingUsers.some(u => (u._id || u.id) === uid);
    setPendingUsers(prev =>
      isAlreadyPending
        ? prev.filter(u => (u._id || u.id) !== uid)
        : [...prev, user]
    );
    // Clear the search input after selecting a user so the bar resets
    if (!isAlreadyPending) {
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const isPending = (user) => pendingUsers.some(u => (u._id || u.id) === (user._id || user.id));

  // Add all pending users with the selected group role (creates invitations)
  const handleAddGroup = async () => {
    if (!pendingUsers.length || !draftId || !versionId) return;
    setAdding(true);
    const token = localStorage.getItem('rv_access_token');
    const invited = [];
    for (const user of pendingUsers) {
      try {
        const res = await fetch(
          `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              userId: user._id || user.id,
              role: groupRole,
              message: inviteMessage.trim() || undefined,
              dueDate: inviteDueDate || undefined,
              expirationDate: inviteExpirationDate || undefined,
            }),
          }
        );
        if (res.ok) {
          const data = await res.json();
          invited.push({ ...data.invitation, username: user.username });
        }
      } catch { /* ignore individual failures */ }
    }
    if (invited.length) {
      const roleLabel = ROLE_CONFIG[groupRole]?.label || groupRole;
      setAddedFeedback(`✓ Invited ${invited.length} ${invited.length === 1 ? 'person' : 'people'} as ${roleLabel}`);
      setOutgoingInvites(prev => [...prev, ...invited]);
    }
    // Reset selection for next group, keep dialog open
    setPendingUsers([]);
    setSearchQuery('');
    setSearchResults([]);
    setGroupRole('design');
    setInviteMessage('');
    setInviteDueDate('');
    setInviteExpirationDate('');
    setAdding(false);
  };

  // Remove collaborator
  const handleRemove = async (userId) => {
    try {
      const token = localStorage.getItem('rv_access_token');
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators/${userId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        onCollaboratorsChange?.(collaborators.filter(c => c.userId !== userId));
      }
    } catch { /* ignore */ }
  };

  // Collaborator manage dialog state
  const [manageCollab, setManageCollab] = useState(null); // { userId, username, currentRole }
  const [selectedDialogRole, setSelectedDialogRole] = useState('design');
  const [roleChanging, setRoleChanging] = useState(false);
  const [removingCollab, setRemovingCollab] = useState(false);

  const handleOpenManageDialog = (c) => {
    if (!isAuthor) return;
    setManageCollab({ userId: c.userId, username: c.username, currentRole: c.role });
    setSelectedDialogRole(c.role);
  };

  const handleCloseManageDialog = () => {
    if (roleChanging || removingCollab) return;
    setManageCollab(null);
  };

  const handleRoleChangeConfirm = async () => {
    if (!manageCollab || selectedDialogRole === manageCollab.currentRole) {
      setManageCollab(null);
      return;
    }
    setRoleChanging(true);
    await handleRoleChange(manageCollab.userId, selectedDialogRole);
    setRoleChanging(false);
    setManageCollab(null);
  };

  const handleRemoveFromDialog = async () => {
    if (!manageCollab) return;
    setRemovingCollab(true);
    await handleRemove(manageCollab.userId);
    setRemovingCollab(false);
    setManageCollab(null);
  };

  // Change collaborator role
  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('rv_access_token');
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators/${userId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ role: newRole }),
        }
      );
      if (res.ok) {
        onCollaboratorsChange?.(collaborators.map(c => c.userId === userId ? { ...c, role: newRole } : c));
      }
    } catch { /* ignore */ }
  };

  const getAvatarLetter = (name) => (name || 'U')[0].toUpperCase();

  if (!versionId) return null;

  // Headless mode: render only the dialogs (used when caller triggers the add dialog externally)
  const headless = hideButton && hideList;

  return (
    <Box sx={headless ? { display: 'contents' } : (embedded ? {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      padding: '0.5rem 0',
      flexWrap: 'wrap',
      mb: 0,
    } : {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      padding: '0.5rem 1rem',
      backgroundColor: '#faf9ff',
      borderRadius: '10px',
      border: '1px solid #e0dff4',
      flexWrap: 'wrap',
      mb: 2,
    })}>
      {!hideList && (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
        <GroupIcon sx={{ color: '#5A52A3', mr: 0.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4a5568' }}>
          Collaborators:
        </Typography>
      </Box>
      )}

      {/* Existing collaborator avatars */}
      {!hideList && (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {collaborators.length > 0 ? (
          collaborators.map((c) => {
            const config = ROLE_CONFIG[c.role] || ROLE_CONFIG.view_only;
            return (
              <Tooltip
                key={c.userId}
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{c.username}</Typography>
                    <Typography variant="caption" sx={{ color: config.border }}>
                      {config.icon} {config.label}
                    </Typography>
                    {isAuthor && (
                      <Typography variant="caption" display="block" sx={{ color: '#ccc', fontSize: '0.65rem', mt: 0.5 }}>
                        Click to manage
                      </Typography>
                    )}
                  </Box>
                }
                arrow
              >
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    src={profilePictures[c.userId] || undefined}
                    onClick={() => handleOpenManageDialog(c)}
                    sx={{
                      width: 36, height: 36,
                      bgcolor: config.color,
                      border: `2px solid ${config.border}`,
                      cursor: isAuthor ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': isAuthor ? { transform: 'scale(1.1)' } : {},
                    }}
                  >
                    {!profilePictures[c.userId] && getAvatarLetter(c.username)}
                  </Avatar>
                  <Box sx={{
                    position: 'absolute', bottom: -2, right: -2,
                    fontSize: '0.6rem', backgroundColor: config.bg,
                    borderRadius: '50%', width: 14, height: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {config.icon}
                  </Box>

                </Box>
              </Tooltip>
            );
          })
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No collaborators assigned
          </Typography>
        )}
      </Box>
      )}

      {!headless && <Box sx={{ flexGrow: 1 }} />}

      {/* Add Collaborators button (author only) */}
      {isAuthor && !hideButton && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenDialog}
          sx={{
            borderColor: '#5A52A3', color: '#5A52A3', borderRadius: '10px',
            '&:hover': { borderColor: '#3B3478', backgroundColor: 'rgba(90, 82, 163, 0.08)' },
          }}
        >
          Add Collaborator
        </Button>
      )}

      {/* Manage Collaborator Dialog */}
      <Dialog
        open={Boolean(manageCollab)}
        onClose={handleCloseManageDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>Manage Collaborator</Typography>
          <IconButton size="small" onClick={handleCloseManageDialog} disabled={roleChanging || removingCollab}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {manageCollab && (() => {
            const currentConfig = ROLE_CONFIG[manageCollab.currentRole] || ROLE_CONFIG.view_only;
            return (
              <Box sx={{ pt: 0.5 }}>
                {/* Collaborator identity */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Avatar
                    src={profilePictures[manageCollab.userId] || undefined}
                    sx={{ width: 42, height: 42, bgcolor: currentConfig.color, border: `2px solid ${currentConfig.border}` }}
                  >
                    {!profilePictures[manageCollab.userId] && getAvatarLetter(manageCollab.username)}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>{manageCollab.username}</Typography>
                    <Chip
                      label={`${currentConfig.icon} ${currentConfig.label}`}
                      size="small"
                      sx={{ bgcolor: currentConfig.bg, color: currentConfig.color, border: `1px solid ${currentConfig.border}`, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </Box>

                {/* Role picker */}
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                  Assign role:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  {Object.entries(ROLE_CONFIG).map(([roleKey, cfg]) => {
                    const isSelected = selectedDialogRole === roleKey;
                    return (
                      <Box
                        key={roleKey}
                        onClick={() => setSelectedDialogRole(roleKey)}
                        sx={{
                          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                          gap: 0.75, py: 2, px: 1, borderRadius: '10px', cursor: 'pointer',
                          border: `2px solid ${isSelected ? cfg.border : '#e5e7eb'}`,
                          backgroundColor: isSelected ? cfg.bg : '#f9fafb',
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: cfg.border, backgroundColor: cfg.bg },
                        }}
                      >
                        {roleKey === 'design'
                          ? <BrushIcon sx={{ color: isSelected ? cfg.color : '#9ca3af', fontSize: 26 }} />
                          : <VisibilityIcon sx={{ color: isSelected ? cfg.color : '#9ca3af', fontSize: 26 }} />}
                        <Typography variant="body2" fontWeight={700} sx={{ color: isSelected ? cfg.color : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
                          {cfg.label === 'Viewer' ? 'View Only' : cfg.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isSelected ? cfg.color : '#9ca3af', fontSize: '0.68rem', textAlign: 'center', textTransform: 'none' }}>
                          {roleKey === 'design' ? 'Can edit & design the cover' : 'Can view & provide feedback'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            startIcon={removingCollab ? <CircularProgress size={14} sx={{ color: '#ef4444' }} /> : <DeleteOutlineIcon />}
            onClick={handleRemoveFromDialog}
            disabled={roleChanging || removingCollab}
            sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' }, fontWeight: 600 }}
          >
            Remove
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseManageDialog}
              disabled={roleChanging || removingCollab}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRoleChangeConfirm}
              disabled={roleChanging || removingCollab}
              sx={{ bgcolor: '#5A52A3', '&:hover': { bgcolor: '#3B3478' }, borderRadius: '8px', minWidth: 130 }}
            >
              {roleChanging ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Change'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Unified Add Collaborators Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>Add Collaborators</Typography>
          <IconButton size="small" onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Success feedback */}
          {addedFeedback && (
            <Box sx={{
              mb: 2, p: 1, backgroundColor: '#d1fae5',
              borderRadius: '6px', border: '1px solid #a7f3d0',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <CheckIcon sx={{ color: '#047857', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 500 }}>
                {addedFeedback}
              </Typography>
            </Box>
          )}

          {/* Search bar */}
          <Box sx={{ position: 'relative', mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search users by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              InputProps={{
                endAdornment: searching ? <CircularProgress size={16} /> : null,
              }}
            />
            {/* Search results dropdown */}
            {(searchResults.length > 0 || (!searching && searchQuery.trim() && searchResults.length === 0)) && (
              <Box sx={{
                position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5,
                bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20,
                maxHeight: 200, overflow: 'auto',
              }}>
                {searchResults.length > 0 ? searchResults.map((user) => {
                  const selected = isPending(user);
                  return (
                    <Box
                      key={user._id || user.id}
                      onClick={() => handleTogglePending(user)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1, cursor: 'pointer',
                        backgroundColor: selected ? '#ede9fe' : 'transparent',
                        '&:hover': { bgcolor: selected ? '#ddd6fe' : '#f5f5f5' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem', bgcolor: '#5A52A3' }}>
                          {getAvatarLetter(user.username)}
                        </Avatar>
                        <Typography variant="body2">{user.username}</Typography>
                      </Box>
                      {selected && <CheckIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />}
                    </Box>
                  );
                }) : (
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No users found
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Pending (selected) users */}
          {pendingUsers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Selected ({pendingUsers.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                {pendingUsers.map((user) => (
                  <Chip
                    key={user._id || user.id}
                    label={user.username}
                    size="small"
                    onDelete={() => handleTogglePending(user)}
                    avatar={<Avatar sx={{ bgcolor: '#5A52A3', fontSize: '0.7rem' }}>{getAvatarLetter(user.username)}</Avatar>}
                    sx={{ backgroundColor: '#ede9fe', color: '#5A52A3', borderColor: '#c4b5fd' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Role picker — always visible */}
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
            Assign role for this group:
          </Typography>
          <ToggleButtonGroup
            value={groupRole}
            exclusive
            onChange={(_, val) => val && setGroupRole(val)}
            fullWidth
            sx={{ mb: 1.5 }}
          >
            <ToggleButton
              value="design"
              sx={{
                display: 'flex', flexDirection: 'column', gap: 0.5, py: 1.5,
                '&.Mui-selected': { backgroundColor: '#ede9fe', borderColor: '#8b5cf6', color: '#8b5cf6' },
              }}
            >
              <BrushIcon />
              <Typography variant="body2" fontWeight={600}>Design</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', textTransform: 'none' }}>
                Can edit & design the cover
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="view_only"
              sx={{
                display: 'flex', flexDirection: 'column', gap: 0.5, py: 1.5,
                '&.Mui-selected': { backgroundColor: '#e0f2fe', borderColor: '#0ea5e9', color: '#0ea5e9' },
              }}
            >
              <VisibilityIcon />
              <Typography variant="body2" fontWeight={600}>View Only</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', textTransform: 'none' }}>
                Can view & provide feedback
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <Chip
            label={groupRole === 'design'
              ? '🎨 Can work on the canvas, save progress, and export'
              : '👁️ Can view the canvas and leave feedback comments'}
            variant="outlined"
            sx={{
              borderColor: ROLE_CONFIG[groupRole].border,
              color: ROLE_CONFIG[groupRole].color,
              backgroundColor: ROLE_CONFIG[groupRole].bg,
              fontSize: '0.75rem', width: '100%', justifyContent: 'flex-start',
            }}
          />

          {/* Due Date + Expiration Date */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
            <TextField
              label="Due Date"
              type="date"
              size="small"
              fullWidth
              value={inviteDueDate}
              onChange={e => setInviteDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Request Expiration Date"
              type="date"
              size="small"
              fullWidth
              value={inviteExpirationDate}
              onChange={e => setInviteExpirationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Assignment detail / message */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 0.75 }}>
              Assignment Detail <Typography component="span" variant="caption" sx={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</Typography>
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder=""
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ fontSize: '0.9rem' }}
            />
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', textAlign: 'right', mt: 0.25 }}>
              {inviteMessage.length} / 500
            </Typography>
          </Box>

          {/* Outgoing invitation statuses */}
          {outgoingInvites.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                Sent invitations
              </Typography>
              {outgoingInvites.map((inv) => {
                const statusColors = { pending: '#f59e0b', accepted: '#16a34a', rejected: '#ef4444' };
                const statusLabels = { pending: 'Pending', accepted: 'Accepted', rejected: 'Rejected' };
                const expired = inv.status === 'pending' && isExpired(inv.expirationDate);
                const overdue = inv.status === 'pending' && isOverdue(inv.dueDate);
                return (
                  <Box
                    key={inv.id}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 0.75, px: 1, mb: 0.5,
                      borderRadius: '6px', backgroundColor: '#f9fafb',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#5A52A3' }}>
                        {(inv.toUsername || inv.username || 'U')[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">{inv.toUsername || inv.username || 'Unknown'}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {expired && (
                        <Chip size="small" label="Expired" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 700 }} />
                      )}
                      {!expired && overdue && (
                        <Chip size="small" label="Overdue" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fef3c7', color: '#d97706', fontWeight: 700 }} />
                      )}
                      <Chip
                        size="small"
                        label={statusLabels[inv.status] || inv.status}
                        sx={{
                          fontWeight: 600, fontSize: '0.68rem', height: 20,
                          backgroundColor: statusColors[inv.status] ? `${statusColors[inv.status]}20` : '#e5e7eb',
                          color: statusColors[inv.status] || '#6b7280',
                          border: `1px solid ${statusColors[inv.status] || '#d1d5db'}`,
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#6b7280' }}>
            Done
          </Button>
          <Button
            onClick={handleAddGroup}
            variant="contained"
            disabled={adding || pendingUsers.length === 0}
            sx={{
              backgroundColor: ROLE_CONFIG[groupRole]?.color || '#5A52A3',
              '&:hover': { filter: 'brightness(0.9)', backgroundColor: ROLE_CONFIG[groupRole]?.color },
            }}
          >
            {adding
              ? 'Inviting...'
              : `Invite ${pendingUsers.length > 0 ? pendingUsers.length : ''} as ${ROLE_CONFIG[groupRole]?.label}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
